import { BaseField, FieldOverride, TemplateDoc } from '../types/geneva-schema';
import { resolveFieldOverridesRecursively, getArrayFieldInfo } from './geneva-schema-helpers';

export interface FieldWithMetadata extends BaseField {
  isRequired: boolean;
  hasDefault: boolean;
  isReadOnly: boolean;
  isHidden: boolean;
  defaultValue?: any;
  helpText?: string;
  options?: string[];
  // Add unique identifier for array fields to avoid conflicts
  uniqueFieldId?: string;
  arrayContext?: string;
}

export interface MappingField extends BaseField {
  originalXmlTag: string;
  uniqueFieldId?: string;
  arrayContext?: string;
}

const hasOwn = Object.prototype.hasOwnProperty;

type ArrayInfo = ReturnType<typeof getArrayFieldInfo>;

const getQuickImportKey = (field: BaseField): string | undefined => {
  const rawKey = field.quickImportColumn;
  if (!rawKey) {
    return undefined;
  }

  const firstLine = rawKey.split('\n')[0].trim();
  return firstLine || undefined;
};

const getOverrideKeyCandidates = (
  field: BaseField,
  arrayInfo?: ArrayInfo,
): string[] => {
  const keys: string[] = [];
  if (arrayInfo) {
    keys.push(`${arrayInfo.arrayName}.${field.xmlTag}`);
  }

  const quickImportKey = getQuickImportKey(field);
  if (quickImportKey) {
    keys.push(quickImportKey);
  }

  keys.push(field.xmlTag);

  // Remove duplicates while preserving order
  return Array.from(new Set(keys.filter(Boolean)));
};

export const findFieldOverride = (
  field: BaseField,
  overrides: Record<string, FieldOverride> = {},
  arrayInfo?: ArrayInfo,
): FieldOverride | undefined => {
  const candidates = getOverrideKeyCandidates(field, arrayInfo);

  for (const key of candidates) {
    if (!key) continue;
    if (hasOwn.call(overrides, key)) {
      return overrides[key];
    }
  }

  return undefined;
};

/**
 * Gets visible fields for a record, applying template overrides
 * This function reuses the same logic as GenevaRecordForm
 */
export function getVisibleFieldsForRecord(
  recordName: string,
  allFields: BaseField[],
  template: TemplateDoc
): FieldWithMetadata[] {
  // Resolve field overrides recursively (handles virtual records)
  const recordOverrides = resolveFieldOverridesRecursively(recordName, template);

  return allFields
    .map(field => {
      const arrayInfo = getArrayFieldInfo(field);
      const override = findFieldOverride(field, recordOverrides, arrayInfo);

      // Create unique identifier for array fields to avoid conflicts with regular fields
      const uniqueFieldId = arrayInfo
        ? `${arrayInfo.arrayName}.${field.xmlTag}`
        : field.xmlTag;

      return {
        ...field,
        isRequired: override?.required ?? field.required,
        hasDefault: override?.default !== undefined,
        isReadOnly: override?.readOnly ?? false,
        isHidden: override?.hidden ?? false,
        defaultValue: override?.default,
        helpText: override?.help,
        options: override?.options,
        uniqueFieldId,
        arrayContext: arrayInfo?.arrayName,
      };
    })
    .filter(field => {
      // Hide fields marked as hidden (same logic as GenevaRecordForm)
      return !field.isHidden;
    });
}

/**
 * Converts FieldWithMetadata to the format expected by BulkFieldMapping
 */
export function convertToMappingFields(fields: FieldWithMetadata[]): MappingField[] {
  return fields.map(field => ({
    xmlTag: field.uniqueFieldId || field.xmlTag, // Use unique ID for array fields
    type: field.type,
    required: field.isRequired,
    multiple: field.multiple,
    description: field.arrayContext
      ? `[${field.arrayContext}] ${field.description || field.xmlTag}`
      : field.description,
    quickImportColumn: field.quickImportColumn,
    references: field.references,
    links: field.links,
    originalXmlTag: field.xmlTag,
    uniqueFieldId: field.uniqueFieldId || field.xmlTag,
    arrayContext: field.arrayContext,
  }));
}
